import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cls } from './cls'

export type ButtonVariant = 'default' | 'primary' | 'ghost'
export type ButtonSize = 'sm' | 'md'

// The soft press comes from the old .btn rules, which sat in a different part
// of app.css from the rest of the button styling and so were easy to miss. The
// reduced-motion block there neutralises transitions for everything under
// .app, so this needs no media query of its own.
const BASE =
  'inline-flex items-center gap-1.5 rounded-none border font-medium cursor-pointer ' +
  'transition-[transform,border-color,background-color] duration-[var(--dur-fast)] ease-[ease] ' +
  'hover:-translate-y-px active:translate-y-0 ' +
  'disabled:opacity-35 disabled:cursor-not-allowed'

const VARIANT: Record<ButtonVariant, string> = {
  default: 'bg-white text-ink-1000 border-border hover:border-ink-700',
  primary: 'bg-ink-900 text-paper border-ink-900 hover:bg-ink-700 hover:border-ink-700',
  ghost: 'bg-transparent text-ink-1000 border-border hover:border-ink-700',
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-[5px] text-xs',
  md: 'px-3.5 py-2 text-[13px]',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  children?: ReactNode
}

/**
 * The app's button. Replaces the .btn / .btn-primary / .btn-ghost / .btn-sm
 * rules that used to live in app.css.
 *
 * `className` is for additions that do not collide with what the variant
 * already sets (layout, width, margin). There is no tailwind-merge here, so a
 * colliding utility resolves by stylesheet order rather than by string order —
 * anything genuinely variable is a prop instead.
 */
export function Button({
  variant = 'default',
  size = 'md',
  type = 'button',
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cls(BASE, VARIANT[variant], SIZE[size], className)}
      {...rest}
    />
  )
}
